import React, { useCallback, useEffect, useMemo } from 'react';
import { DatePicker, Space, TimePicker, Typography } from '@duxton-web/ui';
import dayjs from 'dayjs';
import { useParams } from 'react-router-dom';

import { TIMEZONE } from '@/constants/dateTime';
import {
  getGeography,
  getRestrictByConstraint,
  isTimeSliceStrategy,
  isViewMode,
} from '@/features/experiment/constants';
import { useExperimentContext } from '@/features/experiment/contexts/experiment-context';
import { useGetExperimentAvailability } from '@/hooks';
import Service from '@/lib/App';
import {
  formatUnixTimeInSingaporeTimezone,
  parseUnixTimeFromSingaporeTimezone,
} from '@/utils/dateTime';

const VERSION_TWO_TICKETING_PACKAGE = 2;
const VERSION_THREE_X_SCHEDULER = 3;

const FOOTER_CONTAINER_STYLE = {
  fontSize: '12px',
  padding: '6px',
  maxWidth: '320px',
  lineHeight: '1.2',
} as const;

const LEGEND_ROW_STYLE = {
  display: 'flex',
  alignItems: 'flex-start',
  margin: '0',
  padding: '0',
} as const;

const LEGEND_COL_STYLE = {
  display: 'flex',
  flexDirection: 'column',
  gap: '0px',
} as const;

const LEGEND_TEXT_STYLE = {
  fontSize: '11px',
  lineHeight: '1.1',
  margin: '0',
  padding: '0',
} as const;

function makeSwatch(color: string) {
  return {
    backgroundColor: color,
    borderRadius: '3px',
    height: '6px',
    width: '6px',
    marginTop: '1px',
    marginRight: '4px',
    flexShrink: 0,
  } as const;
}

const OVERLAPPING_FOOTER = (
  <div style={FOOTER_CONTAINER_STYLE}>
    <div style={{ margin: '0 0 2px 0' }}>
      * Higher availability can be expected based on the time
    </div>
    <div style={LEGEND_COL_STYLE}>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#D9F7BE')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - 100% of the current population is available
        </Typography.Text>
      </div>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#FFFB7F')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - Percentage of population available without additional approval
        </Typography.Text>
      </div>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#FFCCC7')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - No population available for the experiment
        </Typography.Text>
      </div>
    </div>
  </div>
);

const STANDARD_FOOTER = (
  <div style={FOOTER_CONTAINER_STYLE}>
    <div style={{ margin: '0 0 2px 0' }}>
      * Higher availability can be expected based on the time
    </div>
    <div style={LEGEND_COL_STYLE}>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#D9F7BE')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - 100% of the current population is available
        </Typography.Text>
      </div>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#FFFB7F')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - Percentage of population available without approval, any percentage
          above will require approval from overlapping experiments
        </Typography.Text>
      </div>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#FFD591')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - Percentage of the current population is available but needs approval
          from overlapping experiments. Any percentage above will exceed our
          total population
        </Typography.Text>
      </div>
      <div style={LEGEND_ROW_STYLE}>
        <div style={makeSwatch('#FFCCC7')} />
        <Typography.Text style={LEGEND_TEXT_STYLE}>
          - No population available for the experiment
        </Typography.Text>
      </div>
    </div>
  </div>
);

const DateTimePicker = ({
  id,
  disabled,
  value,
  onChange,
}: {
  id?: string;
  disabled?: boolean;
  value?: dayjs.Dayjs;
  onChange?: (value: dayjs.Dayjs | undefined) => void;
}) => {
  const params = useParams<{ action: string }>();
  const { action } = params;
  const readOnly = isViewMode(action);

  const experimentContext = useExperimentContext();
  const { experiment, variables, configurations } = experimentContext;
  const experimentDomain = experiment?.domain;
  const experimentAssignmentStrategy = configurations?.[0]?.[0]?.strategy;
  const restrictByConstraint = getRestrictByConstraint(experiment);
  const geography = getGeography(experimentDomain);

  // Bridge the real-moment Dayjs value to Singapore wall-clock for display.
  // AntD pickers always render in browser-local TZ, so we shift the value so
  // its local fields equal the Singapore wall-clock fields.
  const displayValue = useMemo(
    () =>
      value
        ? dayjs(parseUnixTimeFromSingaporeTimezone(value.unix()))
        : undefined,
    [value],
  );

  const overlappingDomain =
    Service.cache.isDomainOverlapSupported(experimentDomain);
  const version =
    overlappingDomain || geography === 'multi'
      ? VERSION_THREE_X_SCHEDULER
      : VERSION_TWO_TICKETING_PACKAGE;

  const { data, refetch } = useGetExperimentAvailability(
    experimentDomain,
    restrictByConstraint?.value,
    overlappingDomain,
    isTimeSliceStrategy(experimentAssignmentStrategy),
    version,
    experiment?.name,
    {
      enabled: !!experimentDomain && !!restrictByConstraint?.value,
    },
  );

  useEffect(() => {
    if (!!experimentDomain && !!restrictByConstraint?.value) {
      refetch();
    }
  }, [experimentDomain, restrictByConstraint?.value, refetch]);

  // When the user picks a time, the picker emits a Dayjs whose local fields
  // represent what the user typed (treated as Singapore wall-clock per label).
  // Convert it back to a real-moment Dayjs so upstream code stays correct.
  const handleChange = useCallback(
    (next?: dayjs.Dayjs | null) => {
      if (!onChange) return;
      if (!next) {
        onChange(undefined);
        return;
      }
      onChange(dayjs.unix(formatUnixTimeInSingaporeTimezone(next)));
    },
    [onChange],
  );

  const renderDate = useCallback(
    (current: dayjs.Dayjs) => {
      let val = '';
      let status = '';
      let partial = false;
      const dateStr = current?.format('YYYY-MM-DD');
      if (data?.availability?.[dateStr]) {
        const avlInfo = data?.availability?.[dateStr];
        val = `${Math.round(avlInfo.availability * 100)}%`;
        status = avlInfo.status;
        partial = avlInfo.partialOccupancy;
      }
      return (
        <div className="ant-picker-cell-inner">
          {current?.date()}
          {val !== '' ? (
            <div className={`experiment-duration__availability ${status}`}>
              {val}
              <span className="experiment-duration__partial">
                {partial ? '*' : ''}
              </span>
            </div>
          ) : null}
        </div>
      );
    },
    [data],
  );

  const disableDate = useCallback((current: dayjs.Dayjs | undefined) => {
    if (!current) return false;
    const todaySGT = dayjs().tz(TIMEZONE).startOf('day');
    const currentAsLocal = dayjs(current.format('YYYY-MM-DD'));
    return currentAsLocal < todaySGT;
  }, []);

  const renderOverlappingFooter = useCallback(
    () =>
      Service.cache.isDomainOverlapSupported(experimentDomain) ||
      geography === 'multi'
        ? OVERLAPPING_FOOTER
        : STANDARD_FOOTER,
    [experimentDomain, geography],
  );

  return (
    <Space.Compact id={id}>
      <DatePicker
        placeholder="Select Date"
        value={displayValue}
        onChange={handleChange}
        disabled={readOnly || variables.length === 0 || disabled}
        disabledDate={disableDate}
        cellRender={renderDate}
        renderExtraFooter={renderOverlappingFooter}
      />
      <TimePicker
        placeholder="Select Time"
        format="HH:mm [GMT +8]"
        value={displayValue}
        onChange={handleChange}
        disabled={readOnly || variables.length === 0 || disabled}
        size="middle"
        allowClear
        needConfirm={false}
      />
    </Space.Compact>
  );
};

export default DateTimePicker;
